import { useEffect, useRef, useState } from "react";
import { checkMediaUrl, detectNativeProvider } from "@kinop/shared";
import { createRoom, type CreateRoomResult } from "../lib/api.js";
import {
  getRoomDefaults,
  getNickname,
  setNickname,
  saveHostToken,
  bumpStats,
} from "../lib/storage.js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RoomRules } from "../components/RoomRules.js";
import { TelegramInvite } from "../components/TelegramInvite.js";
export function NewRoomPage({
  onNavigate,
}: {
  onNavigate: (path: string) => void;
}): JSX.Element {
  const initial = new URLSearchParams(location.search);
  const [name, setName] = useState(initial.get("name") ?? "");
  const [url, setUrl] = useState(initial.get("v") ?? "");
  const [nickname, setNick] = useState(getNickname);
  const [rules, setRules] = useState(getRoomDefaults);
  const [access, setAccess] = useState<"link" | "public" | "approval">("link");
  const [created, setCreated] = useState<CreateRoomResult | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const creation = useRef<Promise<CreateRoomResult> | null>(null);
  const provider = url ? detectNativeProvider(url) : null;
  const check = url ? checkMediaUrl(url, location.protocol) : null;
  useEffect(() => {
    let disposed = false;
    creation.current ??= createRoom({
      name: "Наш киносеанс",
      controlMode: rules.anyonePause ? "everyone" : "host",
      settings: { ...rules, access: "link" },
    });
    void creation.current
      .then((result) => {
        saveHostToken(result.room.code, result.hostToken);
        if (!disposed) {
          setCreated(result);
          bumpStats({ roomsCreated: 1 });
        }
      })
      .catch(() => {
        if (!disposed)
          setError(
            "Не удалось создать комнату. Обновите страницу, чтобы повторить.",
          );
      });
    return () => {
      disposed = true;
    };
  }, []);
  const link = created ? `${location.origin}/r/${created.room.code}` : "";
  async function save() {
    if (!created) return;
    setBusy(true);
    setError("");
    try {
      if (nickname.trim().length < 2)
        throw new Error("Введите имя: хотя бы две буквы.");
      if (url.trim() && !provider) throw new Error('Вставьте ссылку на YouTube, Rutube, VK Видео или Vimeo.');
      if (check && !check.ok && !check.isWarning)
        throw new Error(check.message);
      const response = await fetch(`/api/rooms/${created.room.code}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${created.hostToken}`,
        },
        body: JSON.stringify({
          name: name.trim() || "Наш киносеанс",
          controlMode: rules.anyonePause ? "everyone" : "host",
          settings: { ...rules, access },
        }),
      });
      if (!response.ok)
        throw new Error("Настройки не сохранились. Попробуйте ещё раз.");
      setNickname(nickname.trim());
      onNavigate(
        `/r/${created.room.code}${url.trim() ? `?v=${encodeURIComponent(url.trim())}` : ""}`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не удалось открыть комнату");
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="shell">
      <h1>Новая комната</h1>
      <p className="helper">
        Сначала позовите своих. Фильм и правила можно выбрать вместе.
      </p>
      <div className="new-layout">
        <div>
          <section className="new-step">
            <div className="step-label">
              <span>01</span>Ссылка уже готова
            </div>
            <div className="invite__row">
              <Input
                readOnly
                value={link}
                aria-label="Ссылка-приглашение"
                placeholder="Создаём комнату…"
              />
              <Button
                disabled={!link}
                onClick={() =>
                  void navigator.clipboard
                    .writeText(link)
                    .then(() => {
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1800);
                    })
                    .catch(() =>
                      setError("Выделите и скопируйте ссылку из поля."),
                    )
                }
              >
                {copied ? "Скопировано" : "Копировать"}
              </Button>
            </div>
            {created && (
              <TelegramInvite
                code={created.room.code}
                name={name || "Наш киносеанс"}
              />
            )}
          </section>
          <section className="new-step">
            <label className="step-label" htmlFor="room-video">
              <span>02</span>Что смотрим?
            </label>
            <Input
              id="room-video"
              type="url"
              placeholder="Ссылка на YouTube, Rutube, VK Видео или Vimeo"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
            <p className="helper">
              {provider
                ? `Узнали ${provider.label} — откроем встроенный плеер. Синхронизация зависит от возможностей площадки.`
                : check && !check.ok
                  ? check.message
                  : "Только ссылки на видеоплощадки. Управление — в их родных плеерах."}
            </p>
          </section>
          <section className="new-step">
            <label className="step-label" htmlFor="room-name">
              <span>03</span>Как назовём?
            </label>
            <Input
              id="room-name"
              maxLength={64}
              placeholder="Пятничный сеанс без названия"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <label className="helper" htmlFor="room-nickname">
              Ваше имя
            </label>
            <Input
              id="room-nickname"
              maxLength={24}
              autoComplete="nickname"
              placeholder="Например, Саша"
              value={nickname}
              onChange={(e) => setNick(e.target.value)}
            />
          </section>
          <section className="new-step">
            <div className="step-label">
              <span>04</span>Кого пускаем?
            </div>
            <div className="choice-row">
              {(
                [
                  ["link", "По ссылке"],
                  ["public", "Всех желающих"],
                  ["approval", "По одобрению"],
                ] as const
              ).map(([key, title]) => (
                <button
                  key={key}
                  aria-pressed={access === key}
                  onClick={() => setAccess(key)}
                >
                  {title}
                </button>
              ))}
            </div>
            <p className="helper">
              {access === "public"
                ? "Появится в открытых комнатах."
                : access === "approval"
                  ? "Ведущий подтверждает вход каждого нового гостя."
                  : "Только те, кому вы отправили ссылку. В общем списке её нет."}
            </p>
          </section>
          <section className="new-step">
            <div className="step-label">
              <span>05</span>Наши правила
            </div>
            <RoomRules value={rules} onChange={setRules} />
          </section>
        </div>
        <aside className="guest-preview">
          <p className="mono-label">Что увидят гости</p>
          <div className="guest-preview__frame">▷</div>
          <h2>{name || "Наш киносеанс"}</h2>
          <p className="helper">
            {provider?.label ??
              (url ? "Видео по ссылке" : "Видео ещё не выбрано")}
          </p>
          <p className="helper">
            {rules.anyonePause
              ? "Паузу может ставить каждый"
              : "Управляет ведущий"}
            <br />
            {rules.drawing
              ? `Можно рисовать · след ${rules.trailSeconds} с`
              : "Без рисования"}
            <br />
            {rules.reactions ? "Реакции включены" : "Без реакций"}
          </p>
          <Button
            className="w-full"
            size="lg"
            disabled={!created || busy}
            onClick={() => void save()}
          >
            {busy ? "Открываем…" : "Войти в комнату"}
          </Button>
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
        </aside>
      </div>
    </main>
  );
}
